import type { ReactNode } from "react";
import type {
  CustomerHoldState,
  PortalWording,
} from "@shared-core/domain/portal-sign-in/types";
import type {
  PortalSignInActions,
  PortalSignInFlow,
} from "@shared-core/domain/portal-sign-in/usePortalSignIn";
import { Button } from "../primitives/button";
import { Card } from "../primitives/card";
import { ChoosePartyScreen } from "./ChoosePartyScreen";
import { CodeScreen } from "./CodeScreen";
import {
  ByInvitationScreen,
  CreatedScreen,
  InviteExpiredScreen,
  RefusedScreen,
  RequestSentScreen,
  SignedOutScreen,
  WayRefusedScreen,
} from "./OutcomeScreens";
import { PasskeyOfferScreen } from "./PasskeyOfferScreen";
import { SignInFrame } from "./SignInFrame";
import { SignInScreen } from "./SignInScreen";

export interface SignInFlowProps {
  flow: PortalSignInFlow;
  actions: PortalSignInActions;
  wording: PortalWording;
  /**
   * A customer whose portal staff have put on hold. The wording is the block's own and lives with the customer
   * portal's existing page, so the shell supplies it; a portal without such a block never gets the state.
   */
  renderCustomerHold?(state: CustomerHoldState): ReactNode;
}

/** The screen the sign-in is on, from the state machine's answer. Draws nothing of its own beyond loading and failure. */
export function SignInFlow({
  flow,
  actions,
  wording,
  renderCustomerHold,
}: SignInFlowProps) {
  if (flow.status === "starting" || flow.status === "signed_in") {
    return (
      <SignInFrame eyebrow={wording.brand} title="One moment">
        <p className="text-center text-sm text-muted-foreground">
          Checking your sign-in&hellip;
        </p>
      </SignInFrame>
    );
  }
  if (flow.status === "failed") {
    return (
      <SignInFrame
        eyebrow={wording.brand}
        title="We couldn't sign you in"
        subtitle="Something went wrong reaching us. Nothing has been changed."
      >
        <Card className="p-5">
          <Button className="w-full" onClick={actions.retry}>
            Try again
          </Button>
        </Card>
      </SignInFrame>
    );
  }
  const { screen, busy } = flow;
  switch (screen.screen) {
    case "sign_in":
      return (
        <SignInScreen
          state={screen}
          wording={wording}
          busy={busy}
          onProvider={actions.chooseProvider}
          onPasskey={actions.usePasskey}
          onEmailCode={actions.emailMeACode}
          onDifferentAddress={actions.useDifferentAddress}
          onSignInAs={actions.signInAs}
        />
      );
    case "code":
      return (
        <CodeScreen
          state={screen}
          wording={wording}
          busy={busy}
          onSubmit={actions.submitCode}
          onResend={() => actions.resendCode(screen.address)}
          onDifferentAddress={actions.useDifferentAddress}
        />
      );
    case "passkey_offer":
      return (
        <PasskeyOfferScreen
          wording={wording}
          busy={busy}
          onSave={actions.savePasskey}
          onSkip={actions.skipPasskeyOffer}
        />
      );
    case "choose":
      return (
        <ChoosePartyScreen
          state={screen}
          wording={wording}
          busy={busy}
          onChoose={actions.chooseParty}
        />
      );
    case "created":
      return (
        <CreatedScreen
          state={screen}
          wording={wording}
          busy={busy}
          onContinue={actions.continueAfterCreated}
        />
      );
    case "by_invitation":
      return (
        <ByInvitationScreen
          state={screen}
          wording={wording}
          busy={busy}
          onAskForAccess={actions.askForAccess}
          onUseAnotherAddress={actions.useDifferentAddress}
        />
      );
    case "invite_expired":
      return (
        <InviteExpiredScreen
          state={screen}
          wording={wording}
          busy={busy}
          onAskForAccess={actions.askForAccess}
        />
      );
    case "request_sent":
      return <RequestSentScreen state={screen} wording={wording} />;
    case "refused":
      return (
        <RefusedScreen
          state={screen}
          wording={wording}
          onUseAnotherAddress={actions.useDifferentAddress}
        />
      );
    case "way_refused":
      return (
        <WayRefusedScreen
          state={screen}
          wording={wording}
          busy={busy}
          onProvider={() => actions.chooseProvider("microsoft")}
          onEmailCode={actions.emailMeACode}
          onUseAnotherAddress={actions.useDifferentAddress}
        />
      );
    case "signed_out":
      return (
        <SignedOutScreen
          state={screen}
          wording={wording}
          onSignInAgain={actions.useDifferentAddress}
        />
      );
    case "customer_hold":
      return <>{renderCustomerHold?.(screen) ?? null}</>;
  }
}
